import React from 'react';
import { RiskMeterDto } from '../../../../dtos/RiskMeterDto';
import './RiskMeter.css';

interface OverallRiskTooltipProps {
    data: RiskMeterDto;
}

const riskLevelName = (score: number): string => {
    if (score >= 2) return 'Very high risk';
    if (score >= 1) return 'High risk';
    if (score >= 0) return 'Medium risk';
    if (score >= -1) return 'Moderate risk';
    return 'Low risk';
};

// Explains how the overall score was reached, including the escalation rule when it applied.
export default function OverallRiskTooltip({ data }: OverallRiskTooltipProps) {
    const phaseSlices = data.slices.filter(s => s.label !== 'Buffer' && s.score !== null && s.projected > 0);
    const totalWeight = phaseSlices.reduce((sum, s) => sum + s.projected, 0);
    const totalWeighted = phaseSlices.reduce((sum, s) => sum + (s.score ?? 0) * s.projected, 0);
    const performanceRisk = totalWeight > 0 ? totalWeighted / totalWeight : 0;
    const bufferSlice = data.slices.find(s => s.label === 'Buffer');
    const financialRisk = data.financial?.score ?? data.financialRisk ?? null;

    const cellStyle = { padding: '2px 4px' };
    const numberStyle = { ...cellStyle, textAlign: 'right' as const };

    const average = financialRisk !== null
        ? (performanceRisk + data.bufferRisk + financialRisk) / 3
        : (performanceRisk + data.bufferRisk) / 2;

    return (
        <div style={{ padding: '10px', minWidth: '300px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '10px', borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
                Overall Risk Calculation
            </div>

            <div style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Performance Risk (Weighted Average)</div>
                <table className="table table-sm risk-meter-table" style={{ fontSize: '12px', marginBottom: 0 }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '2px 4px' }}>Phase</th>
                            <th style={numberStyle}>Score</th>
                            <th style={numberStyle}>Weight</th>
                            <th style={numberStyle}>Weighted</th>
                        </tr>
                    </thead>
                    <tbody>
                        {phaseSlices.map((slice, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={cellStyle}>{slice.label}</td>
                                <td style={numberStyle}>{slice.score}</td>
                                <td style={numberStyle}>{slice.projected} mo</td>
                                <td style={numberStyle}>{(slice.score ?? 0) * slice.projected}</td>
                            </tr>
                        ))}
                        <tr style={{ fontWeight: 'bold', borderTop: '1px solid #ccc' }}>
                            <td style={cellStyle}>Total</td>
                            <td></td>
                            <td style={numberStyle}>{totalWeight} mo</td>
                            <td style={numberStyle}>{totalWeighted}</td>
                        </tr>
                    </tbody>
                </table>
                <div style={{ marginTop: '5px', fontSize: '12px' }}>
                    Performance Risk = {totalWeighted} / {totalWeight} = <strong>{performanceRisk.toFixed(2)}</strong>
                </div>
            </div>

            <div style={{ marginBottom: '10px', fontSize: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Buffer Risk</div>
                {bufferSlice
                    ? <>Buffer remaining: {bufferSlice.actual?.toFixed(1)} months &rarr; Score: <strong>{bufferSlice.score}</strong></>
                    : <>Score: <strong>{data.bufferRisk}</strong></>}
            </div>

            <div style={{ marginBottom: '10px', fontSize: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Financial Risk</div>
                {financialRisk !== null
                    ? <>Available funds ratio: {data.financial?.ratio?.toFixed(2) ?? '-'} &rarr; Score: <strong>{financialRisk}</strong></>
                    : <>No financial data entered, so it is left out of the average.</>}
            </div>

            <div style={{ borderTop: '1px solid #ccc', paddingTop: '10px', fontSize: '12px' }}>
                {financialRisk !== null ? (
                    <>
                        Overall = (Performance + Buffer + Financial) / 3<br />
                        Overall = ({performanceRisk.toFixed(2)} + {data.bufferRisk} + {financialRisk}) / 3 = <strong>{average.toFixed(2)}</strong>
                    </>
                ) : (
                    <>
                        Overall = (Performance + Buffer) / 2<br />
                        Overall = ({performanceRisk.toFixed(2)} + {data.bufferRisk}) / 2 = <strong>{average.toFixed(2)}</strong>
                    </>
                )}
            </div>

            {data.riskEscalated && (
                <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fff4e5', border: '1px solid #ffcc80', fontSize: '12px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Raised by the risk rule</div>
                    Timing risk (Performance + Buffer) / 2 = <strong>{data.timingRisk}</strong>
                    {financialRisk !== null && <>, Financial risk = <strong>{financialRisk}</strong></>}
                    <br />
                    Either one reaching high risk sets the overall to at least that level, so the
                    average of {average.toFixed(2)} was raised.
                </div>
            )}

            <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #ccc', fontWeight: 'bold' }}>
                Overall Risk = <span style={{ fontSize: '16px' }}>{data.overallScore}</span> ({riskLevelName(data.overallScore)})
            </div>

            {data.isComplete && (
                <div style={{ marginTop: '8px', fontSize: '12px', fontStyle: 'italic', color: '#2E7D32' }}>
                    Construction is complete, so these scores are final.
                </div>
            )}
        </div>
    );
}
